import React, {useEffect, useRef, useState, useMemo} from 'react'
import {AdyenCheckout, Dropin, Card, PayPal, Klarna} from '@adyen/adyen-web/auto'
import '@adyen/adyen-web/styles/adyen.css'
import useAdyenCheckout from '../../../hooks/useAdyenCheckout'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {Spinner, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    getConvertedCurrencyCode,
    getConvertedPrice,
    getOrderConversionRate,
    formatPrice
} from '@app/utils/price-utils'

export const getCheckoutConfig = (
    adyenEnvironment,
    adyenPaymentMethods,
    translations,
    locale,
    countryCode
) => {
    const checkoutConfig = {
        environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
        clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
        paymentMethodsResponse: adyenPaymentMethods,
        locale: locale.id,
        countryCode: countryCode
    }
    if (translations) {
        checkoutConfig.translations = translations
    }
    return checkoutConfig
}

export const handleQueryParams = (
    urlParams,
    checkout,
    setAdyenPaymentInProgress,
    paymentContainer,
    paymentMethodsConfiguration
) => {
    const redirectResult = urlParams.get('redirectResult')
    const amazonCheckoutSessionId = urlParams.get('amazonCheckoutSessionId')
    const adyenAction = urlParams.get('adyenAction')

    if (redirectResult) {
        checkout.submitDetails({details: {redirectResult}})
    } else if (amazonCheckoutSessionId) {
        setAdyenPaymentInProgress(true)
        const amazonPayContainer = document.createElement('div')
        const amazonPay = checkout
            .create('amazonpay', {
                amazonCheckoutSessionId,
                showOrderButton: false
            })
            .mount(amazonPayContainer)
        amazonPay.submit()
    } else if (adyenAction) {
        const actionString = atob(adyenAction)
        const action = JSON.parse(actionString)
        checkout.createFromAction(action).mount(paymentContainer.current)
    } else {
        new Dropin(checkout, {
            paymentMethodsConfiguration,
            paymentMethodComponents: [Card, PayPal, Klarna]
        }).mount(paymentContainer.current)
    }
}

const AdyenCheckoutComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        getPaymentMethodsConfiguration,
        setAdyenStateData,
        getTranslations,
        locale,
        adyenPaymentInProgress,
        setAdyenPaymentInProgress,
        navigate,
        authToken,
        customerId,
        site
    } = useAdyenCheckout()
    const {data: basket} = useCurrentBasket()
    const [localOrderNo, setLocalOrderNo] = useState(null)
    const intl = useIntl()

    const orderNo = basket?.c_orderNo || localOrderNo
    const paymentContainer = useRef(null)
    const redirectReturnHandledRef = useRef(false)

    const [adyenCheckout, setAdyenCheckout] = useState(null)
    const [paymentMethodsConfig, setPaymentMethodsConfig] = useState(null)

    // @app/components/order-summary': TotalsSummary => Total
    const formattedAmount = useMemo(() => {
        if (!basket) return null

        const conversionsRate = getOrderConversionRate(basket)

        const total =
            basket?.c_internationalShipping && basket?.c_internationalShippingOrderQuote
                ? basket?.c_basketTotalWithGiftCertificatesApplied != null
                    ? basket?.c_basketTotalWithGiftCertificatesApplied
                    : basket?.c_internationalShippingOrderQuote
                : getConvertedPrice(
                      basket?.c_basketTotalWithGiftCertificatesApplied != null
                          ? basket?.c_basketTotalWithGiftCertificatesApplied
                          : basket?.orderTotal,
                      conversionsRate,
                      false
                  )

        return formatPrice(
            total,
            getConvertedCurrencyCode(conversionsRate, basket?.currency),
            conversionsRate,
            basket?.c_internationalShippingCountryCode,
            intl
        )
    }, [
        basket?.c_basketTotalWithGiftCertificatesApplied,
        basket?.orderTotal,
        basket?.currency,
        basket?.c_internationalShipping,
        basket?.c_internationalShippingOrderQuote,
        basket?.c_internationalShippingCountryCode,
        basket?.c_conversionRate,
        basket?.c_lcpMultiplier,
        basket?.c_paymentCurrencyCode,
        basket?.c_usePriceConverter
    ])

    useEffect(() => {
        // The environment (clientKey) is required to create the Adyen checkout.
        if (!authToken || !adyenEnvironment?.ADYEN_CLIENT_KEY) return
        // On a redirect/action return (e.g. Klarna) the checkout must initialize so the
        // redirect details can be submitted, even though payment methods and the country
        // code are only fetched/provided on the standard checkout page.
        const returnParams = new URLSearchParams(location.search)
        const isRedirectReturn = Boolean(
            returnParams.get('redirectResult') ||
                returnParams.get('adyenAction') ||
                returnParams.get('amazonCheckoutSessionId')
        )
        if (!isRedirectReturn && (!adyenPaymentMethods || !props.countryCode)) return
        initializeAdyenCheckout()

        // Hoisted declarations below
        async function initializeAdyenCheckout() {
            try {
                const paymentMethodsConfiguration = await getPaymentMethodsConfiguration({
                    ...props,
                    adyenPaymentMethods,
                    token: authToken,
                    navigate,
                    basket,
                    orderNo,
                    setOrderNo: setLocalOrderNo,
                    adyenOrder: props.adyenOrder,
                    setAdyenOrder: props.setAdyenOrder
                })
                setPaymentMethodsConfig(paymentMethodsConfiguration)

                // get translations with custom 'pay with amount' title
                const translations = getTranslations(formattedAmount)

                const checkoutConfig = getCheckoutConfig(
                    adyenEnvironment,
                    adyenPaymentMethods,
                    translations,
                    locale,
                    props.countryCode
                )

                const checkout = await AdyenCheckout({
                    ...checkoutConfig,
                    onSubmit(state, element, actions) {
                        const onSubmit =
                            paymentMethodsConfiguration.onSubmit ||
                            paymentMethodsConfiguration.card.onSubmit
                        onSubmit(state, element, actions)
                    },
                    onAdditionalDetails(state, element, actions) {
                        const onAdditionalDetails =
                            paymentMethodsConfiguration.onAdditionalDetails ||
                            paymentMethodsConfiguration.card.onAdditionalDetails
                        onAdditionalDetails(state, element, actions)
                    },
                    onChange: (state) => {
                        if (state.isValid) {
                            setAdyenStateData(state.data)
                        }
                    },
                    onError: (error) => {
                        // binLookup errors are handled by the card component, not checkout error
                        if (error?.errorType === 'binLookup') {
                            return
                        }
                        const urlParams = new URLSearchParams(window.location.search)
                        const isRedirectReturn = Boolean(urlParams.get('redirectResult'))

                        navigate(
                            isRedirectReturn
                                ? '/checkout/error?paymentDetailsFailed=true'
                                : '/checkout/error'
                        )
                    }
                })

                setAdyenCheckout(checkout)
            } catch (error) {
                // Do not navigate to the error page on a redirect return: the order is
                // completed server-side by the Adyen webhook regardless, so a transient
                // init hiccup must not send the shopper to /checkout/error.
                console.error('Error initializing Adyen Checkout:', error)
            }
        }

        // formattedAmount
        // case: amount changed due to c_basketTotalWithGiftCertificatesApplied => trigger AdyenCheckout recreating with new translations (amount)
    }, [authToken, adyenEnvironment?.ADYEN_CLIENT_KEY, adyenPaymentMethods, formattedAmount])

    useEffect(() => {
        if (!adyenCheckout || !paymentMethodsConfig) return

        const urlParams = new URLSearchParams(location.search)
        const isRedirectReturn = Boolean(
            urlParams.get('redirectResult') || urlParams.get('adyenAction')
        )
        if (isRedirectReturn) {
            if (redirectReturnHandledRef.current) {
                return
            }

            redirectReturnHandledRef.current = true
        }
        const createCheckout = async () => {
            handleQueryParams(
                urlParams,
                adyenCheckout,
                setAdyenPaymentInProgress,
                paymentContainer,
                paymentMethodsConfig
            )
        }
        if (adyenEnvironment && paymentContainer.current && !adyenPaymentInProgress) {
            // TODO: temporary solution, to be removed or updated after it's fixed in the adyen-web package
            if (window?.paypal?.firstElementChild) {
                window.paypal = undefined
            }

            createCheckout()
        }
    }, [adyenEnvironment, adyenPaymentMethods, adyenCheckout, paymentMethodsConfig])

    useEffect(() => {
        if (!adyenCheckout) return

        updatePayment()

        // Hoisted declarations below
        async function updatePayment() {
            adyenCheckout.update({
                paymentMethodsResponse: adyenPaymentMethods
            })
        }
    }, [basket?.c_basketTotalWithGiftCertificatesApplied, adyenCheckout, adyenPaymentMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <div ref={paymentContainer}></div>
        </>
    )
}

AdyenCheckoutComponent.propTypes = {
    showLoading: PropTypes.bool,
    countryCode: PropTypes.string,
    setOrderNo: PropTypes.func,
    adyenOrder: PropTypes.object,
    setAdyenOrder: PropTypes.func
}

export default AdyenCheckoutComponent
